import { Fragment, useEffect, useState, useRef, useCallback } from "react";
import { ILastResult, scoreboardProvider } from "../data/scoreboard";
import { BrandLockup } from "./BrandLockup";
import { IScoreboardRow, ScoreboardRow } from "./ScoreboardRow";
import { scoreFormatter } from "./scoreFormatter";
import { metricLabelFormatter } from "./metricLabelFormatter";
import { lastParticipantNameFormatter } from "./lastParticipantNameFormatter";
import Settings, { AppSettings, defaultSettings } from "./Settings";
import "../styles/animations.css";
import "../styles/settings.css";

// Helper function to get environment variable overrides from localStorage
const getEnvOverrides = () => {
  const envOverridesStr = localStorage.getItem('scoreboardEnvOverrides');
  return envOverridesStr ? JSON.parse(envOverridesStr) : {};
};

// Get env variable with possible override from localStorage
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  const overrides = getEnvOverrides();
  if (overrides && overrides[key] && overrides[key].trim() !== '') {
    return overrides[key];
  }
  return process.env[key] || defaultValue;
};

// Get env variable as number with possible override
const getEnvVarAsNumber = (key: string, defaultValue: number = 0): number => {
  const value = getEnvVar(key, defaultValue.toString());
  return value ? parseInt(value, 10) : defaultValue;
};

function Scoreboard() {
  const [lastResult, setLastResult] = useState<ILastResult | null>(null);
  const [mensScores, setMensScores] = useState<IScoreboardRow[]>([]);
  const [womensScores, setWomensScores] = useState<IScoreboardRow[]>([]);
  const [countdown, setCountdown] = useState<number>(0);
  const [isShaking, setIsShaking] = useState<boolean>(false);
  const [isTopScore, setIsTopScore] = useState<boolean>(false);
  const [forceTopScoreAnimation, setForceTopScoreAnimation] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [settings, setSettings] = useState<AppSettings>(() => {
    // Load settings from localStorage or use defaults with potential env var overrides
    const savedSettings = localStorage.getItem('scoreboardSettings');
    if (savedSettings) {
      return JSON.parse(savedSettings);
    } else {
      // Use environment variables for defaults if available
      return {
        ...defaultSettings,
        enableTopScoreAnimation: getEnvVar('REACT_APP_ENABLE_TOP_SCORE_ANIMATION', 'true') === 'true',
        enableShakeAnimation: getEnvVar('REACT_APP_ENABLE_SHAKE_ANIMATION', 'true') === 'true',
        forceTopScoreAnimation: getEnvVar('REACT_APP_FORCE_TOP_SCORE_ANIMATION', 'false') === 'true',
        useAutomaticMetricLabel: getEnvVar('REACT_APP_USE_AUTOMATIC_METRIC_LABEL', 'true') === 'true'
      };
    }
  });
  
  const previousScoreRef = useRef<number | null>(null);
  
  // Load settings from localStorage on component mount
  useEffect(() => {
    // Apply force animation setting
    setForceTopScoreAnimation(settings.forceTopScoreAnimation);
    
    // Apply theme
    applyTheme(settings.theme);
  }, [settings.forceTopScoreAnimation, settings.theme]);
  
  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('scoreboardSettings', JSON.stringify(settings));
    
    // If env overrides changed, save them separately
    localStorage.setItem('scoreboardEnvOverrides', JSON.stringify(settings.envOverrides));
    
    // Alert that page reload may be needed if env vars were changed
    const hasEnvChanges = Object.keys(settings.envOverrides).length > 0;
    if (hasEnvChanges) {
      console.log('Environment variable overrides saved. You may need to reload the page for changes to take effect.');
    }
  }, [settings]);
  
  // Add keyboard shortcut to toggle top score animation override
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Use T as the shortcut to toggle the override
      if (e.key === 't') {
        const newValue = !forceTopScoreAnimation;
        setForceTopScoreAnimation(newValue);
        
        // Update settings too
        setSettings(prev => ({
          ...prev,
          forceTopScoreAnimation: newValue
        }));
        
        console.log(`Top score animation override ${newValue ? 'ENABLED' : 'DISABLED'}`);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [forceTopScoreAnimation]);
  
  // Handle saving settings
  const handleSaveSettings = useCallback((newSettings: AppSettings) => {
    setSettings(newSettings);
    setForceTopScoreAnimation(newSettings.forceTopScoreAnimation);
  }, []);
  
  useEffect(() => {
    const subscriber = scoreboardProvider().subscribe(
      (scoreboard) => {
        if (scoreboard !== null) {
          // @ts-ignore - addressing type mismatch between data and component
          setMensScores(scoreboard.male);
          // @ts-ignore - addressing type mismatch between data and component
          setWomensScores(scoreboard.female);
          setLastResult(scoreboard.lastResult);
        }
      },
      (error) => {
        console.error(error);
      }
    );
    return () => subscriber.unsubscribe();
  }, []);

  // Check if the score would be a top score on either scoreboard
  const checkIfTopScore = (score: number, gender: string, athleteId: string): boolean => {
    if (!score) return false;
    
    // Get the relevant scoreboard based on gender
    const scoresList = gender === 'male' ? mensScores : womensScores;
    
    // If there are fewer than 5 scores, it's a top score
    if (scoresList.length < 5) return true;
    
    // Check if this athlete already has a score in the top 5
    const athleteInTop5 = scoresList.some(item => item.id === athleteId);
    
    if (athleteInTop5) {
      // Only a top score if it's better than their current score
      const currentScore = scoresList.find(item => item.id === athleteId)?.score || 0;
      return score > currentScore;
    } else {
      // If athlete not in top 5, check if score beats the lowest score
      const lowestScore = scoresList[scoresList.length - 1].score;
      return score > lowestScore;
    }
  };

  // Track score changes and trigger animation
  useEffect(() => {
    if (lastResult?.score && lastResult.score !== previousScoreRef.current) {
      // Always shake for new scores if shake animation is enabled
      if (settings.enableShakeAnimation) {
        setIsShaking(true);
      }
      
      // Only show top score animation if it's enabled in settings
      if (!settings.enableTopScoreAnimation) {
        setIsTopScore(false);
      } else {
        // If force override is enabled, always show top score animation
        if (forceTopScoreAnimation) {
          setIsTopScore(true);
        } else {
          // Only set isTopScore if this is actually going to be on the leaderboard
          if (lastResult.gender) {
            // If we know the gender, check against the appropriate board
            setIsTopScore(checkIfTopScore(
              lastResult.score, 
              lastResult.gender, 
              lastResult.athleteId || ''
            ));
          } else {
            // If we don't know the gender, check both boards
            // This is a fallback that tries to determine if it's a top score on either board
            const isMaleTopScore = checkIfTopScore(
              lastResult.score, 
              'male', 
              lastResult.athleteId || ''
            );
            const isFemaleTopScore = checkIfTopScore(
              lastResult.score, 
              'female', 
              lastResult.athleteId || ''
            );
            setIsTopScore(isMaleTopScore || isFemaleTopScore);
          }
        }
      }
      
      // Reset animations after they complete
      const timer = setTimeout(() => {
        setIsShaking(false);
        setIsTopScore(false);
      }, 2500); // Allow slightly more time than the animation duration
      
      previousScoreRef.current = lastResult.score;
      return () => clearTimeout(timer);
    }
  }, [lastResult?.score, mensScores, womensScores, forceTopScoreAnimation, settings]);

  const leaderboardStart = getEnvVarAsNumber('REACT_APP_LEADERBOARD_START', 0);
  const leaderboardEnd = getEnvVarAsNumber('REACT_APP_LEADERBOARD_END', 0);

  useEffect(() => {
    const updateCountdown = () => {
      const now = Date.now();
      const timeRemaining = Math.max(0, leaderboardStart - now);
      setCountdown(Math.ceil(timeRemaining / 60000)); // Convert to minutes, rounded up
    };
    
    // Initial update
    updateCountdown();
    
    // Set interval to update every minute
    const interval = setInterval(updateCountdown, 60000);
    
    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, [leaderboardStart]);

  const isLeaderboardOpen = () => {
    let now = Date.now();
    return now >= leaderboardStart && now < leaderboardEnd;
  };

  const isBeforeStart = () => {
    return Date.now() < leaderboardStart;
  }

  const getStartTime = () => {
    return new Date(leaderboardStart).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'America/New_York' 
    });
  }

  const formatCountdown = () => {
    if (countdown < 60) {
      return `${countdown} MINUTE${countdown !== 1 ? 'S' : ''}`;
    } else {
      const hours = Math.floor(countdown / 60);
      const minutes = countdown % 60;
      return `${hours} HOUR${hours !== 1 ? 'S' : ''} ${minutes > 0 ? `${minutes} MINUTE${minutes !== 1 ? 'S' : ''}` : ''}`;
    }
  };

  const showFinalFinishers = () => {
    let now = Date.now();
    return !isLeaderboardOpen() && now > leaderboardEnd;
  };

  // Function to apply the selected theme
  const applyTheme = (themeId: string) => {
    // Remove any existing theme stylesheets
    const existingThemeLinks = document.querySelectorAll('link[data-theme]');
    existingThemeLinks.forEach(link => link.remove());
    
    // Determine which CSS file to load based on the theme ID
    let cssPath;
    switch(themeId) {
      default:
        cssPath = '/themes/cscca-2025/App.css'; // Default
    }
    
    // Create and append the new theme stylesheet
    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = cssPath;
    linkElement.setAttribute('data-theme', 'true');
    document.head.appendChild(linkElement);
  };

  return (
    <Fragment>
      <div className="App-Section Header">
        {/* Add a small visual indicator when override is active */}
        {forceTopScoreAnimation && (
          <div style={{ 
            position: 'absolute', 
            top: '10px', 
            right: '10px', 
            background: 'rgba(19, 123, 198, 0.3)', 
            padding: '4px 8px', 
            borderRadius: '4px', 
            fontSize: '12px',
            zIndex: 999 
          }}>
            EVERYONE WINS MODE
          </div>
        )}
        
        <div className="Brand">
          <div 
            className="Primary-Logo" 
            onClick={() => setSettingsOpen(true)}
            style={{ cursor: 'pointer' }}
          >
            <BrandLockup />
          </div>
          <div className="Secondary-Logo">
            {/* Secondary logo could be added here */}
          </div>
        </div>
        
        <div className="Participant-Info">
        {lastResult?.score ? (
          <div className="Last-Score Name text highlight-text">
            {lastParticipantNameFormatter(lastResult?.name, 16)}
          </div> 
        ) : null}
          <div className="Last-Score">
            {lastResult?.score ? (
              <Fragment>
                <div className="Score-Value" style={{ perspective: '100px', position: 'relative' }}>
                  <div className={`metric ${isShaking ? 'shake-animation' : ''} ${isTopScore ? 'top-score-animation' : ''}`}>
                    {scoreFormatter(lastResult.score)}
                  </div>
                  <div className="units">N/kg</div>
                  {/* {isTopScore && settings.enableTopScoreAnimation && (
                    <div className="top-score-indicator">TOP SCORE!</div>
                  )} */}
                </div>
                {/* Conditionally use either automatic or custom metric label */}
                <div className="Metric-Label text">
                  {settings.useAutomaticMetricLabel 
                    ? metricLabelFormatter(getEnvVar('REACT_APP_METRIC_ID', 'relativePeakForce'))
                    : settings.customMetricLabel || 'METRIC'}
                </div>
              </Fragment>
            ) : null}
          </div>
        </div>

        {!lastResult?.score && isBeforeStart() ? (
          <div className="System-Message highlight-text">
            LEADERBOARD OPENS IN {formatCountdown()} ({getStartTime()} ET)
          </div>
        ) : null}
        
        {/* {!lastResult?.score && isLeaderboardOpen() ? (
          <div className="System-Message highlight-text">SYSTEM READY</div>
        ) : null}
         */}
      
        {showFinalFinishers() && (
          <div className="Final-Finishers highlight-text">
            {"Final Top Finishers: Strike Pad".toUpperCase()}
          </div>
        )}
      </div>
      {/* Hide the Scoreboard until the Leaderboard Opens */}
      { !isBeforeStart() ? (
        <Fragment>
          <div className="App-Section Scoreboard">
            <div className="Scoreboard-Label">FEMALE</div>
            <div className="Scoreboard-Table">
              <table className="Table">
                <tbody className="Table-Body">
                  {womensScores.map((score, index) => {
                    return <ScoreboardRow 
                      rank={index} 
                      {...score} 
                      key={score.id}
                    />;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        
          <div className="App-Section Scoreboard">
            <div className="Scoreboard-Label">MALE</div>
            <div className="Scoreboard-Table">
              <table className="Table">
                <tbody className="Table-Body">
                  {mensScores.map((score, index) => {
                    return <ScoreboardRow 
                      rank={index} 
                      {...score} 
                      key={score.id}
                      units=""
                    />;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Fragment>
      ) : null}

      {/* Add Settings Modal */}
      <Settings 
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSaveSettings={handleSaveSettings}
      />
    </Fragment>
  );
}

export default Scoreboard;
